import React, { useId } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /**
   * `| undefined` explícito por causa de `exactOptionalPropertyTypes`: sem
   * isso, quem calcula o erro condicionalmente não consegue passar `undefined`
   * quando não há erro. Mesma convenção já usada em `@meu-mapa-nev/shared`.
   */
  error?: string | undefined;
  helperText?: string | undefined;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  id: explicitId,
  required,
  style,
  disabled,
  ...props
}) => {
  const generatedId = useId();
  const inputId = explicitId || generatedId;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--nev-space-1)',
    marginBottom: 'var(--nev-space-4)',
    width: '100%',
    minWidth: 0,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: 'var(--nev-font-heading)',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--nev-text-primary)',
  };

  const inputStyle: React.CSSProperties = {
    minHeight: 'var(--nev-control-default-height, 48px)',
    padding: '0 var(--nev-space-3)',
    fontFamily: 'var(--nev-font-body)',
    fontSize: '1rem',
    color: 'var(--nev-text-primary)',
    backgroundColor: 'var(--nev-background-surface)',
    border: `1px solid ${error ? 'var(--nev-color-critical)' : 'var(--nev-border-strong)'}`,
    borderRadius: 'var(--nev-radius-control)',
    width: '100%',
    minWidth: 0,
  };

  const helperStyle: React.CSSProperties = {
    fontFamily: 'var(--nev-font-body)',
    fontSize: '0.8125rem',
    color: 'var(--nev-text-secondary)',
  };

  const errorStyle: React.CSSProperties = {
    fontFamily: 'var(--nev-font-body)',
    fontSize: '0.8125rem',
    color: 'var(--nev-color-critical)',
    fontWeight: 500,
  };

  return (
    <div style={containerStyle}>
      <label htmlFor={inputId} style={labelStyle}>
        {label}
        {required && (
          <span
            aria-hidden="true"
            style={{ color: 'var(--nev-color-critical)', marginLeft: '4px' }}
          >
            *
          </span>
        )}
      </label>
      <input
        id={inputId}
        required={required}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : helperText ? helperId : undefined}
        style={{ ...inputStyle, ...style }}
        {...props}
      />
      {helperText && !error && (
        <span id={helperId} style={helperStyle}>
          {helperText}
        </span>
      )}
      {error && (
        <span id={errorId} role="alert" style={errorStyle}>
          {error}
        </span>
      )}
    </div>
  );
};
